import { View, Text, TextInput, Pressable, ScrollView, StyleSheet, Switch, ActivityIndicator, Platform, Linking, Image, Modal } from 'react-native'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import Animated, { FadeInRight, FadeInDown, withSpring, useSharedValue, useAnimatedStyle } from 'react-native-reanimated'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { ArrowRight, ArrowLeft, Check, CheckCircle, Zap, Smartphone, Wifi, User, ShieldCheck, Mail, FileCheck, MapPin, Copy, Plus, Trash2, Home, Building2, Car, Flame, HelpCircle, X, AlertCircle, ChevronDown, ChevronRight, Search, Inbox } from 'lucide-react-native'
import { apiClient } from '../../src/api/client'
import { storage, USER_ID_KEY, TOKEN_KEY } from '../../src/utils/storage'
import { useAuth } from '../../src/auth/AuthProvider'
import { colors, spacing, borderRadius, typography, shadows } from '../../src/theme'
import { LinearGradient } from 'expo-linear-gradient'
import * as Clipboard from 'expo-clipboard'
import AddressInput from '../../src/components/AddressInput'
import PressableScale from '../../src/components/PressableScale'
import { haptics } from '../../src/utils/haptics'
import { events } from '../../src/lib/posthog'
import { toast } from '../../src/components/Toast'
import { setDemoOnboardingSeen } from '../../src/auth/DevAuthProvider'
import EmailProviderModal from '../../src/components/EmailProviderModal'
export { ScreenErrorBoundary as ErrorBoundary } from '../../src/components/ScreenErrorBoundary'
import { appEvents } from '../../src/lib/events'
import * as WebBrowser from 'expo-web-browser'
import * as ExpoLinking from 'expo-linking'
import { sentryCaptureException } from '../../src/lib/sentry'

// Categories — order matches Next.js: mobile, internet, energy
const CATEGORIES = [
  { id: 'mobile', label: 'Mobil', icon: Smartphone, color: colors.categories.mobile, bg: colors.categoriesBg.mobile },
  { id: 'internet', label: 'Internet', icon: Wifi, color: colors.categories.internet, bg: colors.categoriesBg.internet },
  { id: 'energy', label: 'Strøm', icon: Zap, color: colors.categories.energy, bg: colors.categoriesBg.energy },
]

// kWh estimator table
import { KWH_TABLE, estimateKwh } from '../../src/utils/onboarding'

// Speed tiers
const INTERNET_SPEED_TIERS = [
  { value: '100', label: '100 Mbit', desc: '1-2 pers.' },
  { value: '300', label: '300 Mbit', desc: 'Familie' },
  { value: '1000', label: '1000 Mbit', desc: 'Storforbruger' },
]

const GB_OPTIONS = ['5', '10', '20', '50', '100', 'Fri data']

type StepType = 'categories' | 'how_it_works' | 'energy' | 'mobile' | 'internet' | 'contact' | 'cpr' | 'fuldmagt' | 'email'

const STEP_LABELS: Record<StepType, string> = {
  categories: 'Kategorier',
  how_it_works: 'Sådan virker det',
  energy: 'Strøm',
  mobile: 'Mobil',
  internet: 'Internet',
  contact: 'Kontakt',
  cpr: 'Identitet',
  fuldmagt: 'Fuldmagt',
  email: 'Forbind email',
}

// Fallback providers (used while loading from backend)
const FALLBACK_PROVIDERS: Record<string, string[]> = {
  energy: ['Norlys', 'Andel Energi', 'OK', 'EWII', 'Vindstød', 'Ørsted', 'Seas-NVE', 'Clever', 'NRGi'],
  mobile: ['3 (Tre)', 'Call me', 'CBB Mobil', 'dukaTALE', 'eesy', 'GreenSpeak', 'Greentel', 'Lebara', 'Nettalk', 'OiSTER', 'Telmore', 'Telenor', 'Velkommen', 'YouSee'],
  internet: ['YouSee', 'Fastspeed', 'Hiper', 'Stofa', 'Waoo', 'Norlys', 'Fibia', 'TDC'],
}

const ONBOARDING_STATE_KEY = 'onboarding_draft'

// ─── ProviderSelect ────────────────────────────────────────
function ProviderSelect({ category, value, onChange, providerList }: { category: string; value: string; onChange: (val: string) => void; providerList: string[] }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = providerList.filter(p => p.toLowerCase().includes(search.toLowerCase()))

  const placeholder = `Vælg ${category === 'energy' ? 'strøm' : category === 'mobile' ? 'mobil' : 'internet'}selskab...`

  return (
    <View>
      <Pressable
        style={{
          backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
          borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 10,
          minHeight: 44, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        }}
        onPress={() => { setSearch(''); setOpen(true) }}
      >
        <Text style={{ ...typography.caption, color: value ? colors.text.primary : colors.text.muted }}>
          {value || placeholder}
        </Text>
        <ChevronDown size={16} color={colors.text.muted} />
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' }}
          onPress={() => setOpen(false)}
        >
          <Pressable
            style={{
              backgroundColor: colors.background,
              borderTopLeftRadius: 16, borderTopRightRadius: 16,
              maxHeight: '60%',
              ...Platform.select({ ios: { shadowColor: '#000', shadowOffset: { width: 0, height: -2 }, shadowOpacity: 0.15, shadowRadius: 8 }, android: { elevation: 8 } }),
            }}
            onPress={() => {}}
          >
            {/* Header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.md, paddingTop: spacing.md, paddingBottom: spacing.sm }}>
              <Text style={{ ...typography.captionBold, color: colors.text.primary }}>{placeholder}</Text>
              <Pressable onPress={() => setOpen(false)} hitSlop={12}>
                <X size={20} color={colors.text.muted} />
              </Pressable>
            </View>

            {/* Search */}
            <View style={{ paddingHorizontal: spacing.md, paddingBottom: spacing.sm }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: colors.surface, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, borderWidth: 1, borderColor: colors.border }}>
                <Search size={16} color={colors.text.muted} style={{ marginRight: 6 }} />
                <TextInput
                  style={{ flex: 1, paddingVertical: 10, ...typography.small, fontSize: 16, color: colors.text.primary }}
                  placeholder="Søg selskab..."
                  placeholderTextColor={colors.text.muted}
                  value={search}
                  onChangeText={setSearch}
                  autoFocus
                />
              </View>
            </View>

            {/* List */}
            <ScrollView keyboardShouldPersistTaps="handled" style={{ paddingHorizontal: spacing.md }}>
              <Pressable
                style={{ paddingVertical: 12, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border, backgroundColor: '#f9fafb', paddingHorizontal: spacing.sm, borderRadius: 4 }}
                onPress={() => { onChange(''); setOpen(false) }}
              >
                <Text style={{ ...typography.caption, color: colors.text.muted, fontStyle: 'italic' }}>Ved det ikke</Text>
              </Pressable>
              {filtered.map(p => (
                <Pressable
                  key={p}
                  style={{ paddingVertical: 12, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border, paddingHorizontal: spacing.sm }}
                  onPress={() => { onChange(p); setOpen(false) }}
                >
                  <Text style={{ ...typography.caption, color: value === p ? colors.primary : colors.text.primary, fontWeight: value === p ? '600' : 'normal' }}>{p}</Text>
                </Pressable>
              ))}
              {filtered.length === 0 && search.trim() !== '' && (
                <Pressable
                  style={{ paddingVertical: 12, paddingHorizontal: spacing.sm }}
                  onPress={() => { onChange(search.trim()); setOpen(false) }}
                >
                  <Text style={{ ...typography.caption, color: colors.primary }}>Brug &quot;{search.trim()}&quot;</Text>
                </Pressable>
              )}
              <View style={{ height: 24 }} />
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  )
}

// ─── KwhEstimator (self-contained per-plan) ────────────────
function KwhEstimator({ onEstimate }: { onEstimate: (kwh: number) => void }) {
  const [persons, setPersons] = useState(2)
  const [homeType, setHomeType] = useState<'apartment' | 'house'>('apartment')
  const [hasElHeating, setHasElHeating] = useState(false)
  const [hasEV, setHasEV] = useState(false)

  const estimated = estimateKwh(persons, homeType, hasElHeating, hasEV)

  return (
    <View style={estimatorStyles.box}>
      <Text style={estimatorStyles.title}>Estimer dit årlige forbrug</Text>

      <Text style={[estimatorStyles.fieldLabel, { marginTop: spacing.sm }]}>Antal personer</Text>
      <View style={estimatorStyles.personRow}>
        {[1, 2, 3, 4, 5].map(n => (
          <Pressable
            key={n}
            style={[estimatorStyles.personButton, persons === n && estimatorStyles.personButtonActive]}
            onPress={() => setPersons(n)}
          >
            <Text style={[estimatorStyles.personButtonText, persons === n && estimatorStyles.personButtonTextActive]}>
              {n}{n === 5 ? '+' : ''}
            </Text>
          </Pressable>
        ))}
      </View>

      <View style={estimatorStyles.homeTypeRow}>
        <Pressable
          style={[estimatorStyles.homeTypeButton, homeType === 'apartment' && estimatorStyles.homeTypeActive]}
          onPress={() => setHomeType('apartment')}
        >
          <Building2 size={14} color={homeType === 'apartment' ? colors.warningDark : colors.text.muted} />
          <Text style={[estimatorStyles.homeTypeText, homeType === 'apartment' && { color: colors.warningDark }]}>Lejlighed</Text>
        </Pressable>
        <Pressable
          style={[estimatorStyles.homeTypeButton, homeType === 'house' && estimatorStyles.homeTypeActive]}
          onPress={() => setHomeType('house')}
        >
          <Home size={14} color={homeType === 'house' ? colors.warningDark : colors.text.muted} />
          <Text style={[estimatorStyles.homeTypeText, homeType === 'house' && { color: colors.warningDark }]}>Hus</Text>
        </Pressable>
      </View>

      <View style={estimatorStyles.extrasRow}>
        <Pressable style={estimatorStyles.extraToggle} onPress={() => setHasElHeating(!hasElHeating)}>
          <View style={[estimatorStyles.checkbox, hasElHeating && estimatorStyles.checkboxActive]}>
            {hasElHeating && <Check size={10} color="#fff" />}
          </View>
          <Flame size={12} color={colors.text.secondary} />
          <Text style={estimatorStyles.extraLabel}>El-varme</Text>
        </Pressable>
        <Pressable style={estimatorStyles.extraToggle} onPress={() => setHasEV(!hasEV)}>
          <View style={[estimatorStyles.checkbox, hasEV && estimatorStyles.checkboxActive]}>
            {hasEV && <Check size={10} color="#fff" />}
          </View>
          <Car size={12} color={colors.text.secondary} />
          <Text style={estimatorStyles.extraLabel}>Elbil/lader</Text>
        </Pressable>
      </View>

      <View style={estimatorStyles.result}>
        <Text style={estimatorStyles.kwhText}>
          Ca. {estimated.toLocaleString('da-DK')} kWh/år
        </Text>
        <Pressable
          style={estimatorStyles.useButton}
          onPress={() => {
            onEstimate(estimated)
          }}
        >
          <Text style={estimatorStyles.useText}>Brug estimat</Text>
        </Pressable>
      </View>
    </View>
  )
}

const estimatorStyles = StyleSheet.create({
  toggle: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 },
  toggleText: { ...typography.small },
  box: {
    marginTop: spacing.sm, padding: spacing.sm, backgroundColor: colors.warningMuted,
    borderRadius: borderRadius.sm, borderWidth: 1, borderColor: '#fde68a',
  },
  title: { ...typography.captionBold, color: colors.warningDark },
  fieldLabel: { ...typography.captionBold, color: colors.text.secondary, marginBottom: 4 },
  personRow: { flexDirection: 'row', gap: 6, marginTop: 4 },
  personButton: {
    width: 40, height: 40, borderRadius: 8, backgroundColor: colors.background,
    borderWidth: 1, borderColor: colors.border, alignItems: 'center', justifyContent: 'center',
  },
  personButtonActive: { backgroundColor: colors.warningMuted, borderColor: colors.warning },
  personButtonText: { ...typography.captionBold, color: colors.text.muted },
  personButtonTextActive: { color: colors.warningDark },
  homeTypeRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  homeTypeButton: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
    paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background,
  },
  homeTypeActive: { backgroundColor: colors.warningMuted, borderColor: colors.warning },
  homeTypeText: { ...typography.small, color: colors.text.muted },
  extrasRow: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.sm },
  extraToggle: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  extraLabel: { ...typography.small, color: colors.text.secondary },
  checkbox: {
    width: 18, height: 18, borderRadius: 4, borderWidth: 1.5, borderColor: colors.border,
    alignItems: 'center', justifyContent: 'center',
  },
  checkboxActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  result: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: spacing.sm,
  },
  kwhText: { ...typography.captionBold, color: colors.warningDark },
  useButton: {
    paddingHorizontal: spacing.sm, paddingVertical: 8, backgroundColor: colors.warning,
    borderRadius: borderRadius.sm,
  },
  useText: { ...typography.small, color: '#fff', fontWeight: '600' },
})

export default function OnboardingScreen() {
  const router = useRouter()
  const params = useLocalSearchParams<{ email_connected?: string; preview?: string; step?: string }>()
  const { refreshUser, isAuthenticated, isLoading: authLoading } = useAuth()
  const insets = useSafeAreaInsets()

  // Auth guard — redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.replace('/login' as any)
    }
  }, [authLoading, isAuthenticated])

  // PostHog: onboarding_started — fires once per session when wizard opens
  const onboardingStartedFired = useRef(false)
  useEffect(() => {
    if (!authLoading && isAuthenticated && !onboardingStartedFired.current) {
      events.onboardingStarted()
      onboardingStartedFired.current = true
    }
  }, [authLoading, isAuthenticated])

  const [step, setStep] = useState(() => {
    const s = Number(params.step)
    return s > 0 ? s : 1
  })
  const [loading, setLoading] = useState(false)
  const [showProviderModal, setShowProviderModal] = useState(false)
  const [initialEmailProvider, setInitialEmailProvider] = useState<string | undefined>(undefined)
  const [showSkipEmailModal, setShowSkipEmailModal] = useState(false)
  const [connectingOAuth, setConnectingOAuth] = useState<string | null>(null)
  const [userProfile, setUserProfile] = useState<any>(null)
  const [activeCategoriesFromHunts, setActiveCategoriesFromHunts] = useState<Set<string>>(new Set())

  // Returning user = has completed onboarding before → skip contact/fuldmagt
  const isReturningUser = !!(userProfile?.onboarding_completed && userProfile?.fuldmagt_accepted) && params.preview !== '1'

  // Categories
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['energy', 'mobile', 'internet'])

  // Plans
  const [energyPlans, setEnergyPlans] = useState<{ provider: string; yearly_kwh: string; price: string; payment_type: 'postpaid' | 'prepaid'; same_address: boolean; address: string; hasExisting: boolean }[]>([{ provider: '', yearly_kwh: '', price: '', payment_type: 'postpaid', same_address: true, address: '', hasExisting: true }])
  const [mobilePlans, setMobilePlans] = useState([{ provider: '', price: '', desired_gb_dk: '20', wants_eu_data: false, wants_world_data: false, hasExisting: true }])
  const [internetPlans, setInternetPlans] = useState([{ provider: '', price: '', same_address: true, address: '', needs: '300', hasExisting: true }])
  const [sharedAddress, setSharedAddress] = useState('')

  // Contact
  const [contactData, setContactData] = useState({ fullName: '', email: '', phone: '' })

  // Fuldmagt
  const [consentFuldmagt, setConsentFuldmagt] = useState(false)
  const [consentTerms, setConsentTerms] = useState(false)
  const [forwardToEmail, setForwardToEmail] = useState(false)
  const [agentEmail, setAgentEmail] = useState('din-email@a.forbrugeragenten.dk')

  // CPR fallback (shown when MitID doesn't return SSN)
  const [cprInput, setCprInput] = useState('')
  const [hasCpr, setHasCpr] = useState(false) // true = already in system, skip step

  // Email connection
  const [emailConnected, setEmailConnected] = useState(false)
  const [copiedEmail, setCopiedEmail] = useState(false)
  const [forwardPrefs, setForwardPrefs] = useState({ forward_bills: false, forward_notices: false, forward_marketing: false })

  // Field validation errors (for visual highlighting)
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set())

  // Providers (for ProviderSelect)
  const [providers, setProviders] = useState<Record<string, string[]>>(FALLBACK_PROVIDERS)

  // ScrollView ref for scroll-to-top on step change
  const scrollRef = useRef<ScrollView>(null)

  // Dynamic step list
  const stepList = useMemo<StepType[]>(() => {
    const steps: StepType[] = ['categories', 'how_it_works']
    if (selectedCategories.includes('mobile')) steps.push('mobile')
    if (selectedCategories.includes('internet')) steps.push('internet')
    if (selectedCategories.includes('energy')) steps.push('energy')
    // Returning users already provided contact + fuldmagt — skip
    if (!isReturningUser) {
      steps.push('contact')
      // CPR step only if not already in system
      if (!hasCpr) steps.push('cpr')
      steps.push('fuldmagt', 'email')
    }
    return steps
  }, [selectedCategories, isReturningUser, hasCpr])

  // Clamp step to valid range when stepList changes (e.g. isReturningUser flips)
  useEffect(() => {
    if (step > stepList.length) setStep(stepList.length)
  }, [stepList.length])

  // Show minimum 5 steps in counter so "Trin 1 af 2" doesn't appear when 0 categories
  const totalSteps = Math.max(stepList.length, isReturningUser ? 3 : 5)
  const currentStepType = stepList[step - 1] || 'categories'

  // Restore saved draft state
  useEffect(() => {
    const restoreDraft = async () => {
      try {
        const raw = await storage.get(ONBOARDING_STATE_KEY)
        if (raw) {
          const draft = JSON.parse(raw)
          if (draft.step && !params.step) setStep(draft.step)
          if (draft.selectedCategories) setSelectedCategories(draft.selectedCategories)
          if (draft.sharedAddress) setSharedAddress(draft.sharedAddress)
          if (draft.contactData) setContactData(draft.contactData)
          if (draft.energyPlans) setEnergyPlans(draft.energyPlans)
          if (draft.mobilePlans) setMobilePlans(draft.mobilePlans.map((p: any) => ({ ...p, desired_gb_dk: p.desired_gb_dk || '20' })))
          if (draft.internetPlans) setInternetPlans(draft.internetPlans.map((p: any) => ({ ...p, needs: p.needs || '300' })))
        }
      } catch {}
    }
    restoreDraft()
  }, [])

  // Handle OAuth redirect — sync + poll connections when user returns from email OAuth
  useEffect(() => {
    if (params.email_connected === 'true') {
      // Navigate to the email step if it exists
      const godkendIdx = stepList.indexOf('email')
      if (godkendIdx >= 0) setStep(godkendIdx + 1)
      // Restore consent state lost during OAuth redirect (user already saw fuldmagt step)
      setConsentFuldmagt(true)
      setConsentTerms(true)
      // Sync with Unipile + poll until connection appears
      let attempts = 0
      const poll = async () => {
        attempts++
        try {
          const res = await apiClient.email.sync()
          const connections = res.data?.connections || []
          if (connections.length > 0) {
            setEmailConnected(true)
            toast.success('Email forbundet!')
            const newest = connections.reduce((a: any, b: any) =>
              new Date(b.connected_at) > new Date(a.connected_at) ? b : a
            )
            if (newest?.id && !newest.scan_consent_given_at) {
              apiClient.email.scanConsent(newest.id).catch(() => {})
            }
            return
          }
        } catch {}
        if (attempts < 5) {
          setTimeout(poll, 2000)
        } else {
          toast.error('Vi kunne ikke bekræfte emailforbindelsen — prøv at forbinde igen.')
        }
      }
      poll()
    } else if (params.email_connected === 'false') {
      toast.error('Email tilslutning fejlede')
    }
  }, [params.email_connected])

  // Save draft state on changes (skip when step is forced via URL — UI overview mode)
  useEffect(() => {
    if (step <= 1 || params.step) return
    const draft = { step, selectedCategories, sharedAddress, contactData, energyPlans, mobilePlans, internetPlans }
    storage.set(ONBOARDING_STATE_KEY, JSON.stringify(draft)).catch(() => {})
  }, [step, selectedCategories, sharedAddress, contactData, energyPlans, mobilePlans, internetPlans])

  // Scroll to top + clear errors on step change
  useEffect(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: true })
    setFieldErrors(new Set())
  }, [step])

  // Auto-fill consents if user already has fuldmagt (returning users)
  useEffect(() => {
    if (currentStepType === 'fuldmagt' && userProfile?.fuldmagt_accepted) {
      setConsentFuldmagt(true)
      setConsentTerms(true)
    }
  }, [currentStepType, userProfile?.fuldmagt_accepted])

  // Persist onboarding step to backend (debounced) — matches Next.js
  // Skip when step is forced via URL param (UI overview mode)
  const stepSaveRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    if (step <= 1 || params.step) return
    const save = async () => {
      const uid = await storage.get(USER_ID_KEY)
      if (!uid) return
      if (stepSaveRef.current) clearTimeout(stepSaveRef.current)
      stepSaveRef.current = setTimeout(() => {
        apiClient.users.update(uid, { onboarding_step: currentStepType }).catch(() => {})
      }, 500)
    }
    save()
    return () => { if (stepSaveRef.current) clearTimeout(stepSaveRef.current) }
  }, [step, currentStepType])

  // Load user profile + active hunts + pre-fill data
  useEffect(() => {
    const load = async () => {
      const userId = await storage.get(USER_ID_KEY)
      if (!userId) return
      try {
        const res = await apiClient.users.getMe(userId)
        const u = res.data
        setUserProfile(u)
        if (u.cpr_number) setHasCpr(true) // cpr_number is masked on /auth/me — presence means CPR exists
        if (u.full_name) setContactData(prev => ({ ...prev, fullName: u.full_name || prev.fullName }))
        if (u.email) setContactData(prev => ({ ...prev, email: u.email || prev.email }))
        if (u.phone) setContactData(prev => ({ ...prev, phone: u.phone || prev.phone }))
        if (u.agent_email) setAgentEmail(u.agent_email)
        if (u.address) setSharedAddress(u.address)
        // Restore step from backend if available (matches Next.js)
        // Skip if step is forced via URL param (used by UI overview)
        if (u.onboarding_step && !params.step) {
          const idx = stepList.findIndex(s => s === u.onboarding_step)
          if (idx >= 0) setStep(idx + 1)
        }
      } catch {}

      // Fetch existing hunts to disable active categories + pre-fill
      try {
        const huntsRes = await apiClient.quotes.getRequests(userId)
        const all: any[] = huntsRes.data || []

        // Disable categories with active hunts (skip in dev mode)
        const cats = new Set<string>()
        const isDevMode = __DEV__ || process.env.EXPO_PUBLIC_DEV_MODE === 'true'
        if (!isDevMode) {
          all
            .filter((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
            .forEach((r: any) => r.categories?.forEach((c: string) => cats.add(c)))
          setActiveCategoriesFromHunts(cats)

          const available = ['energy', 'mobile', 'internet'].filter(c => !cats.has(c))
          setSelectedCategories(available)
        }

        // Pre-fill plan data from most recent previous request
        const sorted = [...all].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        const prefillEnergy = sorted.find(r => Array.isArray(r.user_data?.energy) && r.user_data.energy.length > 0)?.user_data?.energy
        const prefillMobile = sorted.find(r => Array.isArray(r.user_data?.mobile) && r.user_data.mobile.length > 0)?.user_data?.mobile
        const prefillInternet = sorted.find(r => Array.isArray(r.user_data?.internet) && r.user_data.internet.length > 0)?.user_data?.internet
        if (prefillEnergy) setEnergyPlans(prefillEnergy)
        if (prefillMobile) setMobilePlans(prefillMobile.map((p: any) => ({ ...p, desired_gb_dk: p.desired_gb_dk || '20' })))
        if (prefillInternet) setInternetPlans(prefillInternet.map((p: any) => ({ ...p, needs: p.needs || '300' })))

        const prefillAddress = sorted.find(r => r.address)?.address
        if (prefillAddress) setSharedAddress(prev => prev || prefillAddress)
      } catch {}
    }
    load()
  }, [])

  // Fetch providers for ProviderSelect
  useEffect(() => {
    let mounted = true
    apiClient.providers.list().then(res => {
      if (!mounted) return
      const data: any[] = res.data || []
      const byCategory: Record<string, string[]> = { energy: [], mobile: [], internet: [] }
      for (const p of data) {
        const cats: string[] = p.categories || []
        for (const cat of cats) {
          if (byCategory[cat] && !byCategory[cat].includes(p.name)) {
            byCategory[cat].push(p.name)
          }
        }
      }
      const merged: Record<string, string[]> = { ...FALLBACK_PROVIDERS }
      for (const cat of ['energy', 'mobile', 'internet']) {
        if (byCategory[cat].length > 0) {
          merged[cat] = byCategory[cat].sort((a, b) => a.localeCompare(b, 'da'))
        }
      }
      setProviders(merged)
    }).catch(() => {})
    return () => { mounted = false }
  }, [])

  // Check email connection status
  useEffect(() => {
    let mounted = true
    apiClient.email.status().then(res => {
      if (!mounted) return
      const connections: any[] = res.data?.connections || []
      if (connections.length > 0) setEmailConnected(true)
    }).catch(() => {})
    return () => { mounted = false }
  }, [])

  const toggleCategory = (id: string) => {
    if (activeCategoriesFromHunts.has(id)) return
    haptics.selection()
    setSelectedCategories(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    )
  }

  // First category step index (for showing reassurance banner)
  const firstCategoryStepIndex = stepList.findIndex(s => s === 'energy' || s === 'mobile' || s === 'internet')

  // Copy agent email to clipboard (matches Next.js)
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => { if (copyTimerRef.current) clearTimeout(copyTimerRef.current) }, [])
  const copyAgentEmail = async () => {
    await Clipboard.setStringAsync(agentEmail)
    setCopiedEmail(true)
    toast.success('Email kopieret!')
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
    copyTimerRef.current = setTimeout(() => setCopiedEmail(false), 2000)
  }

  // Email connected callback from modal
  const handleEmailConnected = () => {
    setEmailConnected(true)
    setShowProviderModal(false)
    // Trigger GDPR-consented scan for newest connection
    apiClient.email.status().then(res => {
      const connections = res.data?.connections || []
      if (connections.length > 0) {
        const newest = connections.reduce((a: any, b: any) =>
          new Date(b.connected_at) > new Date(a.connected_at) ? b : a
        )
        if (newest?.id && !newest.scan_consent_given_at) {
          apiClient.email.scanConsent(newest.id).catch(() => {})
        }
      }
    }).catch(() => {})
  }

  // Inline OAuth connect (Gmail/Outlook) — avoids full modal popup
  const handleConnectOAuthInline = async (providerKey: 'google' | 'microsoft') => {
    if (connectingOAuth) return
    setConnectingOAuth(providerKey)
    try {
      const res = await apiClient.email.authorize(providerKey, 'onboarding')
      if (!res.data?.authorization_url) { toast.error('Serverfejl: kunne ikke oprette forbindelse'); return }
      if (Platform.OS === 'web') {
        window.location.href = res.data.authorization_url
      } else {
        const callbackUrl = ExpoLinking.createURL('onboarding', { queryParams: { email_connected: 'true' } })
        await WebBrowser.openAuthSessionAsync(res.data.authorization_url, callbackUrl)
        for (let i = 0; i < 5; i++) {
          try {
            const syncRes = await apiClient.email.sync()
            if ((syncRes.data?.connections || []).length > 0) { handleEmailConnected(); break }
          } catch {}
          if (i < 4) await new Promise(r => setTimeout(r, 2000))
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke starte email-forbindelsen')
    } finally {
      setConnectingOAuth(null)
    }
  }

  const showValidationToast = (errors: Set<string>) => {
    if (errors.has('energy_kwh')) toast.error('Indtast dit årlige kWh-forbrug for strøm')
    else if (errors.has('energy_kwh_high')) toast.error('kWh-forbrug virker for højt (maks 100.000 kWh)')
    else if (errors.has('mobile_gb')) toast.error('Vælg ønsket dataforbrug for mobil')
    else if (errors.has('internet_speed')) toast.error('Vælg ønsket hastighed for internet')
    else if (errors.has('energy_price')) toast.error('Udfyld din nuværende pris for strøm')
    else if (errors.has('mobile_price')) toast.error('Udfyld din nuværende pris for mobil')
    else if (errors.has('internet_price')) toast.error('Udfyld din nuværende pris for internet')
    else if (errors.has('address')) toast.error('Udfyld din adresse')
    else if (errors.has('fullName')) toast.error('Udfyld dit fulde navn')
    else if (errors.has('email')) toast.error('Udfyld en gyldig email')
    else if (errors.has('phone')) toast.error('Udfyld dit telefonnummer')
    else if (errors.has('cpr')) toast.error('Indtast dit 10-cifrede CPR-nummer')
    else if (errors.has('cpr_invalid')) toast.error('CPR-nummeret ser ikke rigtigt ud — tjek det igen')
  }

  const handleNext = async () => {
    if (currentStepType === 'email' && !emailConnected) {
      setShowSkipEmailModal(true)
      return
    }
    const errors = new Set<string>()

    // CPR: validate + save to backend
    if (currentStepType === 'cpr') {
      const digits = cprInput.replace(/[\s\-]/g, '')
      if (digits.length < 10) { errors.add('cpr') }
      else if (!/^\d{10}$/.test(digits)) { errors.add('cpr_invalid') }
      else {
        const day = parseInt(digits.substring(0, 2), 10)
        const month = parseInt(digits.substring(2, 4), 10)
        if (day < 1 || day > 31 || month < 1 || month > 12) {
          errors.add('cpr_invalid')
        }
      }
      if (errors.size > 0) {
        setFieldErrors(errors)
        showValidationToast(errors)
        haptics.warning()
        return
      }
      // Save CPR to backend
      try {
        setLoading(true)
        await apiClient.onboarding.saveCpr(cprInput.replace(/[\s\-]/g, ''))
        // Note: do NOT setHasCpr(true) here — that would remove 'cpr' from stepList
        // mid-flow and cause step index to skip fuldmagt. hasCpr is only used for
        // initial stepList calculation (from API on page load).
      } catch (e: any) {
        const detail = e?.response?.data?.detail || 'Kunne ikke gemme CPR — prøv igen'
        toast.error(detail)
        return
      } finally {
        setLoading(false)
      }
      setFieldErrors(new Set())
      haptics.light()
      events.onboardingStepCompleted(step, 'cpr')
      setStep(step + 1)
      return
    }

    // Energy: require kWh + price + address
    if (currentStepType === 'energy') {
      const missingKwh = energyPlans.some(p => !p.yearly_kwh || Number(p.yearly_kwh) < 1)
      if (missingKwh) errors.add('energy_kwh')
      const tooHighKwh = energyPlans.some(p => Number(p.yearly_kwh) > 100000)
      if (tooHighKwh) errors.add('energy_kwh_high')
      const missingPrice = energyPlans.some(p => p.hasExisting !== false && !p.price)
      if (missingPrice) errors.add('energy_price')
      const hasAddress = (sharedAddress || '').trim() ||
        energyPlans.every(p => !p.same_address && (p.address || '').trim())
      if (!hasAddress) errors.add('address')
    }

    // Mobile: require GB selection + price (price only if hasExisting)
    if (currentStepType === 'mobile') {
      const missingGb = mobilePlans.some(p => !p.desired_gb_dk)
      if (missingGb) errors.add('mobile_gb')
      const missingPrice = mobilePlans.some(p => p.hasExisting !== false && !p.price)
      if (missingPrice) errors.add('mobile_price')
    }

    // Internet: require speed + price (price only if hasExisting) + address
    if (currentStepType === 'internet') {
      const missingSpeed = internetPlans.some(p => !p.needs)
      if (missingSpeed) errors.add('internet_speed')
      const missingPrice = internetPlans.some(p => p.hasExisting !== false && !p.price)
      if (missingPrice) errors.add('internet_price')
      const hasAddress = (sharedAddress || '').trim() ||
        internetPlans.every(p => !p.same_address && (p.address || '').trim())
      if (!hasAddress) errors.add('address')
    }

    // Contact: require name + email + phone
    if (currentStepType === 'contact') {
      if (!contactData.fullName.trim()) errors.add('fullName')
      if (!contactData.email.trim() || !contactData.email.includes('@')) errors.add('email')
      if (!contactData.phone || contactData.phone.replace(/[^0-9]/g, '').length < 8) errors.add('phone')
    }

    if (errors.size > 0) {
      setFieldErrors(errors)
      showValidationToast(errors)
      haptics.warning()
      return
    }

    // Fuldmagt: persist acceptance to backend immediately so createRequest sees it
    if (currentStepType === 'fuldmagt' && consentFuldmagt && consentTerms) {
      try {
        const uid = await storage.get(USER_ID_KEY)
        if (uid) {
          await apiClient.users.update(uid, { fuldmagt_accepted: true })
        }
      } catch {
        // Non-blocking — handleSubmit will also set it, this is just early
      }
    }

    setFieldErrors(new Set())
    haptics.light()
    events.onboardingStepCompleted(step, currentStepType)
    setStep(step + 1)
  }

  const handleSubmit = async () => {
    // Returning users already accepted fuldmagt — skip consent check
    if (!isReturningUser && (!consentFuldmagt || !consentTerms)) {
      toast.error('Du skal acceptere fuldmagt og vilkår')
      return
    }
    // Collect all validation errors
    const errors = new Set<string>()
    // Validate energy plans
    if (selectedCategories.includes('energy')) {
      const missingKwh = energyPlans.some(p => !p.yearly_kwh || Number(p.yearly_kwh) < 1)
      if (missingKwh) errors.add('energy_kwh')
      const tooHighKwh = energyPlans.some(p => Number(p.yearly_kwh) > 100000)
      if (tooHighKwh) errors.add('energy_kwh_high')
    }
    // Validate mobile plans
    if (selectedCategories.includes('mobile')) {
      const missingGb = mobilePlans.some(p => !p.desired_gb_dk)
      if (missingGb) errors.add('mobile_gb')
    }
    // Validate internet plans
    if (selectedCategories.includes('internet')) {
      const missingSpeed = internetPlans.some(p => !p.needs)
      if (missingSpeed) errors.add('internet_speed')
    }
    if (errors.size > 0) {
      setFieldErrors(errors)
      showValidationToast(errors)
      haptics.warning()
      return
    }
    setFieldErrors(new Set())
    haptics.medium()
    setLoading(true)
    let userId: string | null = null
    try {
      userId = await storage.get(USER_ID_KEY)
      const token = await storage.get(TOKEN_KEY)
      if (!userId || !token) {
        toast.error('Din session er udløbet. Log ind igen.')
        router.replace('/login' as any)
        return
      }

      const isDevSubmit = __DEV__ || process.env.EXPO_PUBLIC_DEV_MODE === 'true'

      // Set fuldmagt_accepted + profile BEFORE creating quote request
      // Backend checks fuldmagt_accepted before sending emails — must be true first
      // This MUST succeed — retry once on failure before giving up
      const profilePayload = !isReturningUser ? {
        full_name: contactData.fullName,
        email: contactData.email,
        phone: contactData.phone,
        address: sharedAddress || undefined,
        onboarding_completed: true,
        fuldmagt_accepted: true,
        ...forwardPrefs,
      } : sharedAddress ? { address: sharedAddress } : null

      if (profilePayload) {
        try {
          await apiClient.users.update(userId, profilePayload)
        } catch (firstErr: any) {
          console.warn('[onboarding] users.update failed:', firstErr?.response?.status, firstErr?.response?.data)
          if (!isDevSubmit) {
            // Retry once after 1s
            await new Promise(r => setTimeout(r, 1000))
            await apiClient.users.update(userId, profilePayload)
          }
          // Dev: log but continue — profile update may fail if fields already set
        }
      }

      // Create quote request — backend now sees fuldmagt_accepted: true
      await apiClient.quotes.createRequest({
        categories: selectedCategories,
        address: sharedAddress,
        user_data: {
          energy: selectedCategories.includes('energy') ? energyPlans : null,
          mobile: selectedCategories.includes('mobile') ? mobilePlans : null,
          internet: selectedCategories.includes('internet') ? internetPlans : null,
        }
      }).catch((e: any) => {
        // 409 = already exists, 429 = rate limited — treat both as success (quote is in progress)
        if (e?.response?.status === 409 || e?.response?.status === 429) return
        console.error('[onboarding] quote request failed:', e?.response?.status, e?.response?.data?.detail || e?.message)
        throw e
      })

      // Track success events
      events.onboardingCompleted(selectedCategories)
      selectedCategories.forEach(cat => events.quoteRequestCreated(cat))

      // Dev mode: mark onboarding as seen (matches Next.js sessionStorage.setItem('demo_onboarding_seen', '1'))
      if (__DEV__ || process.env.EXPO_PUBLIC_DEV_MODE === 'true') {
        await setDemoOnboardingSeen(true)
      }

      // Clear draft and refresh user state
      await storage.remove(ONBOARDING_STATE_KEY)
      // Fire post_onboarding feedback after 10s (direct trigger, no storage relay)
      setTimeout(() => {
        appEvents.emit('open-feedback', { trigger: 'post_onboarding', question: 'Var der noget der var forvirrende?' })
      }, 10_000)
      await refreshUser()

      // Navigate to dashboard
      haptics.success()
      toast.success('Succes! Vi indhenter tilbud nu.')
      router.replace('/(tabs)' as any)
    } catch (error: any) {
      console.error('handleSubmit error:', error)
      if (error?.response?.status === 401) return
      if (error?.response?.status === 409) {
        // Quote already sent — still complete onboarding on user profile
        await apiClient.users.update(userId!, {
          full_name: contactData.fullName,
          email: contactData.email,
          onboarding_completed: true,
          fuldmagt_accepted: true,
        }).catch(() => {})
        await refreshUser()
        haptics.success()
        toast.success('Dine tilbud er allerede undervejs!')
        router.replace('/(tabs)' as any)
        return
      }
      const detail = error?.response?.data?.detail
      if (!detail) sentryCaptureException(error, { action: 'onboarding_submit' })
      toast.error(detail || 'Der skete en fejl — vi er notificeret. Prøv igen.')
    } finally {
      setLoading(false)
    }
  }

  // First address step index
  const firstAddressStepIndex = stepList.findIndex(s => s === 'internet' || s === 'energy')
  const needsAddressOnThisStep = (step - 1) === firstAddressStepIndex && (currentStepType === 'energy' || currentStepType === 'internet')

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.headerTitle}>ForbrugerAgenten</Text>
            <Text style={styles.headerSubtitle}>Din økonomiske vagthund</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {(__DEV__ || process.env.EXPO_PUBLIC_DEV_MODE === 'true') && (
              <Pressable
                style={{ paddingHorizontal: 12, paddingVertical: 6, backgroundColor: '#f59e0b', borderRadius: borderRadius.sm, marginRight: spacing.xs }}
                onPress={async () => { await setDemoOnboardingSeen(true); await refreshUser(); router.replace('/(tabs)' as any) }}
              >
                <Text style={{ ...typography.small, color: '#fff', fontWeight: '600' }}>Skip</Text>
              </Pressable>
            )}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Luk onboarding"
              style={styles.closeButton}
              onPress={() => {
                if (Platform.OS === 'web') {
                  if (window.confirm('Vil du forlade onboarding? Din fremgang gemmes automatisk.')) {
                    router.replace('/(tabs)' as any)
                  }
                } else {
                  const { Alert: NativeAlert } = require('react-native')
                  NativeAlert.alert(
                    'Vil du forlade onboarding?',
                    'Din fremgang gemmes automatisk.',
                    [
                      { text: 'Bliv her', style: 'cancel' },
                      { text: 'Forlad', onPress: () => router.replace('/(tabs)' as any) },
                    ]
                  )
                }
              }}
            >
              <X size={20} color={colors.text.muted} />
            </Pressable>
          </View>
        </View>

        {/* Progress bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressLabels}>
            <Text style={styles.progressLabel}>{STEP_LABELS[currentStepType]}</Text>
          </View>
          <View style={styles.progressTrack}>
            <View style={[styles.progressFill, { width: `${(step / totalSteps) * 100}%` }]} />
          </View>
        </View>

        <ScrollView ref={scrollRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">

          {/* ===== STEP: Categories ===== */}
          {currentStepType === 'categories' && (
            <Animated.View key="categories" entering={FadeInRight.duration(300)} style={styles.card}>
              <Text style={styles.cardTitle}>Hvilke abonnementer har du?</Text>
              {activeCategoriesFromHunts.size > 0 ? (
                <Text style={styles.cardSubtitle}>Vælg de kategorier du vil starte en ny jagt for.</Text>
              ) : (
                <View style={{ marginBottom: spacing.md }}>
                  <Text style={{ ...typography.caption, color: colors.text.primary, fontWeight: '500' }}>Vælg dine abonnementer — vi giver dig overblik og finder de bedste aftaler. Gratis og uforpligtende.</Text>
                  <Text style={{ ...typography.small, color: colors.text.secondary, marginTop: 4 }}>Tryk på en kategori for at fravælge den.</Text>
                </View>
              )}

              <View style={{ gap: spacing.sm }}>
                {CATEGORIES.map(cat => {
                  const Icon = cat.icon
                  const isSelected = selectedCategories.includes(cat.id)
                  const isDisabled = activeCategoriesFromHunts.has(cat.id)
                  return (
                    <PressableScale
                      key={cat.id}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: isSelected, disabled: isDisabled }}
                      accessibilityLabel={`${cat.label}${isDisabled ? ', jagt allerede i gang' : ''}`}
                      style={[
                        styles.categoryCard,
                        { borderColor: isSelected ? cat.color : colors.border },
                        isSelected && { backgroundColor: cat.bg },
                        isDisabled && { opacity: 0.5 },
                      ]}
                      onPress={() => toggleCategory(cat.id)}
                      disabled={isDisabled}
                    >
                      <View style={[styles.categoryIconBox, { backgroundColor: isSelected ? cat.bg : colors.surface }]}>
                        <Icon size={24} color={isSelected ? cat.color : colors.text.muted} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.categoryLabel, isSelected && { color: cat.color }, { flex: 0 }]}>{cat.label}</Text>
                        {isDisabled && <Text style={styles.disabledHint}>Du har allerede en jagt i gang</Text>}
                      </View>
                      {isSelected && !isDisabled && (
                        <View style={[styles.checkCircle, { backgroundColor: cat.color }]}>
                          <Check size={14} color="#fff" />
                        </View>
                      )}
                    </PressableScale>
                  )
                })}
              </View>
            </Animated.View>
          )}

          {/* ===== STEP: How it works ===== */}
          {currentStepType === 'how_it_works' && (
            <Animated.View key="how_it_works" entering={FadeInRight.duration(300)} style={styles.card}>
              <Text style={styles.cardTitle}>Din personlige forbrugeragent</Text>

              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryLight, borderRadius: borderRadius.md, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, marginBottom: spacing.md, borderWidth: 1, borderColor: '#bfdbfe', gap: spacing.xs }}>
                <Zap size={13} color={colors.primary} />
                <Text style={{ fontSize: 13, fontWeight: '600', color: colors.primary }}>
                  Gns. besparelse 4.200 kr./år · 79+ udbydere
                </Text>
              </View>

              {[
                { title: 'Kontakter alle 79+ udbydere', desc: 'Vi finder de bedste tilbud på el, mobil og internet — du læner dig bare tilbage', Icon: Zap },
                { title: 'Alle aftaler samlet ét sted', desc: 'Overblik over regninger, bindinger og al kommunikation med dine selskaber', Icon: Inbox },
                { title: 'Skift med ét tryk via MitID', desc: 'Vi klarer opsigelse og skift — du skal aldrig tale med en telefonsælger igen', Icon: CheckCircle },
              ].map((item, i) => (
                <Animated.View key={i} entering={FadeInDown.delay(i * 80).duration(250)} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.md, padding: spacing.md, backgroundColor: colors.surface, borderRadius: borderRadius.md, marginBottom: spacing.sm }}>
                  <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: colors.primaryLight, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <item.Icon size={16} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ ...typography.bodyBold, color: colors.text.primary, marginBottom: 2 }}>{item.title}</Text>
                    <Text style={{ ...typography.caption, color: colors.text.secondary }}>{item.desc}</Text>
                  </View>
                </Animated.View>
              ))}
            </Animated.View>
          )}

          {/* ===== STEP: Energy ===== */}
          {currentStepType === 'energy' && (
            <Animated.View key="energy" entering={FadeInRight.duration(300)} style={styles.card}>
              {needsAddressOnThisStep && (
                <>
                  <View style={styles.infoBanner}>
                    <Text style={styles.infoBannerText}>
                      Det behøver ikke være 100% præcist — vi kontakter også dit nuværende selskab for præcise aftaledetaljer.
                    </Text>
                  </View>
                  <View style={[styles.fieldGroup, { zIndex: 20 }]}>
                    <Text style={styles.fieldLabel}>Din adresse *</Text>
                    <AddressInput
                      value={sharedAddress}
                      onChange={(v) => { setSharedAddress(v); setFieldErrors(prev => { const n = new Set(prev); n.delete('address'); return n }) }}
                      placeholder="Søg din adresse..."
                      error={fieldErrors.has('address')}
                    />
                    {fieldErrors.has('address') && (
                      <View style={styles.inlineError}>
                        <AlertCircle size={12} color="#ef4444" />
                        <Text style={styles.inlineErrorText}>Indtast din adresse</Text>
                      </View>
                    )}
                  </View>
                </>
              )}

              <View style={styles.categoryHeader}>
                <Zap size={20} color={colors.categories.energy} />
                <Text style={[styles.cardTitle, { color: colors.warningDark, marginBottom: 0 }]}>Strøm</Text>
              </View>

              {energyPlans.map((plan, idx) => (
                <View key={idx} style={[styles.planCard, { backgroundColor: colors.warningLight, borderColor: '#fde68a' }]}>
                  {energyPlans.length > 1 && (
                    <View style={styles.planHeader}>
                      <Text style={{ ...typography.small, color: colors.categories.energy, fontWeight: '600' }}>Aftale {idx + 1}</Text>
                      <Pressable accessibilityRole="button" accessibilityLabel={`Fjern strømaftale ${idx + 1}`} onPress={() => setEnergyPlans(prev => prev.filter((_, i) => i !== idx))}>
                        <Trash2 size={16} color={colors.error} />
                      </Pressable>
                    </View>
                  )}

                  {plan.hasExisting !== false && (
                    <>
                      <Text style={styles.fieldLabel}>Nuværende selskab</Text>
                      <ProviderSelect
                        category="energy"
                        value={plan.provider}
                        onChange={val => setEnergyPlans(prev => prev.map((p, i) => i === idx ? { ...p, provider: val } : p))}
                        providerList={providers.energy || []}
                      />

                      <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>Ca. pris pr. md. (kr) *</Text>
                      <TextInput
                        style={[styles.textInput, fieldErrors.has('energy_price') && { borderColor: colors.error }]}
                        placeholder="kr/md"
                        placeholderTextColor={colors.text.muted}
                        keyboardType="numeric"
                        value={plan.price}
                        onChangeText={val => { setEnergyPlans(prev => prev.map((p, i) => i === idx ? { ...p, price: val.replace(/[^0-9]/g, '') } : p)); setFieldErrors(prev => { const n = new Set(prev); n.delete('energy_price'); return n }) }}
                        maxLength={10}
                      />
                      {fieldErrors.has('energy_price') && (
                        <View style={styles.inlineError}>
                          <AlertCircle size={12} color="#ef4444" />
                          <Text style={styles.inlineErrorText}>Indtast din nuværende månedspris</Text>
                        </View>
                      )}
                    </>
                  )}

                  {/* kWh Estimator — always visible */}
                  <KwhEstimator
                    onEstimate={(kwh) => {
                      setEnergyPlans(prev => prev.map((p, i) => i === idx ? { ...p, yearly_kwh: String(kwh) } : p))
                    }}
                  />

                  <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>Eller indtast selv (kWh/år)</Text>
                  <TextInput
                    style={[styles.textInput, (fieldErrors.has('energy_kwh') || fieldErrors.has('energy_kwh_high')) && { borderColor: colors.error }]}
                    placeholder="fx 4000"
                    placeholderTextColor={colors.text.muted}
                    keyboardType="numeric"
                    value={plan.yearly_kwh}
                    onChangeText={val => { setEnergyPlans(prev => prev.map((p, i) => i === idx ? { ...p, yearly_kwh: val.replace(/[^0-9]/g, '').slice(0, 6) } : p)); setFieldErrors(prev => { const n = new Set(prev); n.delete('energy_kwh'); n.delete('energy_kwh_high'); return n }) }}
                    maxLength={6}
                  />
                  {fieldErrors.has('energy_kwh') && (
                    <View style={styles.inlineError}>
                      <AlertCircle size={12} color="#ef4444" />
                      <Text style={styles.inlineErrorText}>Indtast dit årlige kWh-forbrug</Text>
                    </View>
                  )}
                  {fieldErrors.has('energy_kwh_high') && (
                    <View style={styles.inlineError}>
                      <AlertCircle size={12} color="#ef4444" />
                      <Text style={styles.inlineErrorText}>Maks 100.000 kWh — tjek tallet</Text>
                    </View>
                  )}
                  {!fieldErrors.has('energy_kwh') && !fieldErrors.has('energy_kwh_high') && (
                    <Text style={styles.fieldHint}>Du finder dit forbrug på din elregning {'\u00B7'} typisk 1.500{'\u2013'}8.000 kWh/år</Text>
                  )}
                  {plan.yearly_kwh && (Number(plan.yearly_kwh) < 500 || Number(plan.yearly_kwh) > 30000) && (
                    <View style={styles.kwhWarning}>
                      <Text style={styles.kwhWarningText}>Et normalt forbrug ligger mellem 500 og 30.000 kWh/år — er du sikker?</Text>
                    </View>
                  )}

                  {/* Per-plan address */}
                  <View style={{ marginTop: spacing.sm }}>
                    <Pressable
                      style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}
                      onPress={() => setEnergyPlans(prev => prev.map((p, i) => i === idx ? { ...p, same_address: !p.same_address } : p))}
                    >
                      <View style={[styles.checkbox, plan.same_address && styles.checkboxActive]}>
                        {plan.same_address && <Check size={10} color="#fff" />}
                      </View>
                      <Text style={styles.extraLabel}>Samme adresse</Text>
                    </Pressable>
                    {plan.same_address && sharedAddress ? (
                      <Text style={{ ...typography.small, color: colors.text.muted, marginTop: 4, marginLeft: 26 }}>{sharedAddress}</Text>
                    ) : !plan.same_address ? (
                      <View style={{ marginTop: spacing.xs, zIndex: 10 }}>
                        <AddressInput
                          value={plan.address}
                          onChange={val => setEnergyPlans(prev => prev.map((p, i) => i === idx ? { ...p, address: val } : p))}
                          placeholder="Anden adresse for denne aftale..."
                        />
                      </View>
                    ) : null}
                  </View>

                  {/* "Har ikke endnu?" toggle */}
                  <Pressable
                    style={{ marginTop: spacing.sm, paddingVertical: 4 }}
                    onPress={() => setEnergyPlans(prev => prev.map((p, i) => i === idx ? { ...p, hasExisting: !p.hasExisting, ...(p.hasExisting ? { provider: '', price: '' } : {}) } : p))}
                  >
                    <Text style={{ ...typography.small, color: colors.primary, textDecorationLine: 'underline' }}>
                      {plan.hasExisting !== false ? 'Har ikke strøm endnu?' : 'Har allerede strøm?'}
                    </Text>
                  </Pressable>
                </View>
              ))}

              <Pressable
                style={styles.addPlanButton}
                onPress={() => setEnergyPlans(prev => {
                  const last = prev[prev.length - 1]
                  return [...prev, { provider: last?.provider || '', yearly_kwh: last?.yearly_kwh || '', price: last?.price || '', payment_type: last?.payment_type || 'postpaid', same_address: false, address: '', hasExisting: true }]
                })}
              >
                <Plus size={16} color={colors.categories.energy} />
                <Text style={[styles.addPlanText, { color: colors.categories.energy }]}>Tilføj endnu en strøm-aftale</Text>
              </Pressable>
            </Animated.View>
          )}

          {/* ===== STEP: Mobile ===== */}
          {currentStepType === 'mobile' && (
            <Animated.View key="mobile" entering={FadeInRight.duration(300)} style={styles.card}>
              {step - 1 === firstCategoryStepIndex && (
                <View style={styles.infoBanner}>
                  <Text style={styles.infoBannerText}>
                    Det behøver ikke være 100% præcist — vi kontakter også dit nuværende selskab for præcise aftaledetaljer.
                  </Text>
                </View>
              )}

              <View style={styles.categoryHeader}>
                <Smartphone size={20} color={colors.categories.mobile} />
                <Text style={[styles.cardTitle, { color: colors.primaryDark, marginBottom: 0 }]}>Mobil</Text>
              </View>

              {mobilePlans.map((plan, idx) => (
                <View key={idx} style={[styles.planCard, { backgroundColor: colors.primarySubtle, borderColor: '#bfdbfe' }]}>
                  {mobilePlans.length > 1 && (
                    <View style={styles.planHeader}>
                      <Text style={{ ...typography.small, color: colors.primary, fontWeight: '600' }}>Aftale {idx + 1}</Text>
                      <Pressable accessibilityRole="button" accessibilityLabel={`Fjern mobilaftale ${idx + 1}`} onPress={() => setMobilePlans(prev => prev.filter((_, i) => i !== idx))}>
                        <Trash2 size={16} color={colors.error} />
                      </Pressable>
                    </View>
                  )}

                  {plan.hasExisting !== false && (
                    <>
                      <Text style={styles.fieldLabel}>Nuværende selskab</Text>
                      <ProviderSelect
                        category="mobile"
                        value={plan.provider}
                        onChange={val => setMobilePlans(prev => prev.map((p, i) => i === idx ? { ...p, provider: val } : p))}
                        providerList={providers.mobile || []}
                      />

                      <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>Ca. pris pr. md. (kr) *</Text>
                      <TextInput
                        style={[styles.textInput, fieldErrors.has('mobile_price') && { borderColor: colors.error }]}
                        placeholder="kr/md"
                        placeholderTextColor={colors.text.muted}
                        keyboardType="numeric"
                        value={plan.price}
                        onChangeText={val => { setMobilePlans(prev => prev.map((p, i) => i === idx ? { ...p, price: val.replace(/[^0-9]/g, '') } : p)); setFieldErrors(prev => { const n = new Set(prev); n.delete('mobile_price'); return n }) }}
                        maxLength={10}
                      />
                      {fieldErrors.has('mobile_price') && (
                        <View style={styles.inlineError}>
                          <AlertCircle size={12} color="#ef4444" />
                          <Text style={styles.inlineErrorText}>Indtast din nuværende månedspris</Text>
                        </View>
                      )}
                    </>
                  )}

                  <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>Ønsket data i DK *</Text>
                  <View style={[styles.gbGrid, fieldErrors.has('mobile_gb') && { borderWidth: 1, borderColor: colors.error, borderRadius: borderRadius.md, padding: 4 }]}>
                    {GB_OPTIONS.map(gb => (
                      <Pressable
                        key={gb}
                        accessibilityRole="radio"
                        accessibilityState={{ selected: plan.desired_gb_dk === gb }}
                        accessibilityLabel={gb === 'Fri data' ? 'Fri data' : `${gb} GB`}
                        style={[styles.gbButton, plan.desired_gb_dk === gb && styles.gbButtonActive]}
                        onPress={() => { setMobilePlans(prev => prev.map((p, i) => i === idx ? { ...p, desired_gb_dk: gb } : p)); setFieldErrors(prev => { const n = new Set(prev); n.delete('mobile_gb'); return n }) }}
                      >
                        <Text style={[styles.gbButtonText, plan.desired_gb_dk === gb && styles.gbButtonTextActive]}>
                          {gb === 'Fri data' ? 'Fri' : `${gb} GB`}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                  {fieldErrors.has('mobile_gb') ? (
                    <View style={styles.inlineError}>
                      <AlertCircle size={12} color="#ef4444" />
                      <Text style={styles.inlineErrorText}>Vælg ønsket dataforbrug</Text>
                    </View>
                  ) : (
                    <Text style={styles.fieldHint}>De fleste bruger 10-30 GB</Text>
                  )}

                  <View style={styles.extrasRow}>
                    <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: plan.wants_eu_data }} accessibilityLabel="EU data" style={styles.extraToggle} onPress={() => setMobilePlans(prev => prev.map((p, i) => i === idx ? { ...p, wants_eu_data: !p.wants_eu_data } : p))}>
                      <View style={[styles.checkbox, plan.wants_eu_data && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                        {plan.wants_eu_data && <Check size={10} color="#fff" />}
                      </View>
                      <Text style={styles.extraLabel}>EU data</Text>
                    </Pressable>
                    <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: plan.wants_world_data }} accessibilityLabel="Verden data" style={styles.extraToggle} onPress={() => setMobilePlans(prev => prev.map((p, i) => i === idx ? { ...p, wants_world_data: !p.wants_world_data } : p))}>
                      <View style={[styles.checkbox, plan.wants_world_data && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                        {plan.wants_world_data && <Check size={10} color="#fff" />}
                      </View>
                      <Text style={styles.extraLabel}>Verden data</Text>
                    </Pressable>
                  </View>

                  {/* "Har ikke endnu?" toggle */}
                  <Pressable
                    style={{ marginTop: spacing.sm, paddingVertical: 4 }}
                    onPress={() => setMobilePlans(prev => prev.map((p, i) => i === idx ? { ...p, hasExisting: !p.hasExisting, ...(p.hasExisting ? { provider: '', price: '' } : {}) } : p))}
                  >
                    <Text style={{ ...typography.small, color: colors.primary, textDecorationLine: 'underline' }}>
                      {plan.hasExisting !== false ? 'Har ikke mobil endnu?' : 'Har allerede mobil?'}
                    </Text>
                  </Pressable>
                </View>
              ))}

              <Pressable
                style={styles.addPlanButton}
                onPress={() => setMobilePlans(prev => {
                  const last = prev[prev.length - 1]
                  return [...prev, { provider: last?.provider || '', price: last?.price || '', desired_gb_dk: last?.desired_gb_dk || '20', wants_eu_data: last?.wants_eu_data ?? false, wants_world_data: last?.wants_world_data ?? false, hasExisting: true }]
                })}
              >
                <Plus size={16} color={colors.primary} />
                <Text style={[styles.addPlanText, { color: colors.primary }]}>Tilføj endnu et mobilabonnement</Text>
              </Pressable>
            </Animated.View>
          )}

          {/* ===== STEP: Internet ===== */}
          {currentStepType === 'internet' && (
            <Animated.View key="internet" entering={FadeInRight.duration(300)} style={styles.card}>
              {needsAddressOnThisStep && (
                <>
                  <View style={styles.infoBanner}>
                    <Text style={styles.infoBannerText}>
                      Det behøver ikke være 100% præcist — vi kontakter også dit nuværende selskab for præcise aftaledetaljer.
                    </Text>
                  </View>
                  <View style={[styles.fieldGroup, { zIndex: 20 }]}>
                    <Text style={styles.fieldLabel}>Din adresse *</Text>
                    <AddressInput
                      value={sharedAddress}
                      onChange={(v) => { setSharedAddress(v); setFieldErrors(prev => { const n = new Set(prev); n.delete('address'); return n }) }}
                      placeholder="Søg din adresse..."
                      error={fieldErrors.has('address')}
                    />
                    {fieldErrors.has('address') && (
                      <View style={styles.inlineError}>
                        <AlertCircle size={12} color="#ef4444" />
                        <Text style={styles.inlineErrorText}>Indtast din adresse</Text>
                      </View>
                    )}
                  </View>
                </>
              )}

              <View style={styles.categoryHeader}>
                <Wifi size={20} color={colors.categories.internet} />
                <Text style={[styles.cardTitle, { color: '#6b21a8', marginBottom: 0 }]}>Internet</Text>
              </View>

              {internetPlans.map((plan, idx) => (
                <View key={idx} style={[styles.planCard, { backgroundColor: '#faf5ff', borderColor: '#e9d5ff' }]}>
                  {internetPlans.length > 1 && (
                    <View style={styles.planHeader}>
                      <Text style={{ ...typography.small, color: colors.categories.internet, fontWeight: '600' }}>Aftale {idx + 1}</Text>
                      <Pressable accessibilityRole="button" accessibilityLabel={`Fjern internetaftale ${idx + 1}`} onPress={() => setInternetPlans(prev => prev.filter((_, i) => i !== idx))}>
                        <Trash2 size={16} color={colors.error} />
                      </Pressable>
                    </View>
                  )}

                  {plan.hasExisting !== false && (
                    <>
                      <Text style={styles.fieldLabel}>Nuværende selskab</Text>
                      <ProviderSelect
                        category="internet"
                        value={plan.provider}
                        onChange={val => setInternetPlans(prev => prev.map((p, i) => i === idx ? { ...p, provider: val } : p))}
                        providerList={providers.internet || []}
                      />

                      <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>Ca. pris pr. md. (kr) *</Text>
                      <TextInput
                        style={[styles.textInput, fieldErrors.has('internet_price') && { borderColor: colors.error }]}
                        placeholder="kr/md"
                        placeholderTextColor={colors.text.muted}
                        keyboardType="numeric"
                        value={plan.price}
                        onChangeText={val => { setInternetPlans(prev => prev.map((p, i) => i === idx ? { ...p, price: val.replace(/[^0-9]/g, '') } : p)); setFieldErrors(prev => { const n = new Set(prev); n.delete('internet_price'); return n }) }}
                        maxLength={10}
                      />
                      {fieldErrors.has('internet_price') && (
                        <View style={styles.inlineError}>
                          <AlertCircle size={12} color="#ef4444" />
                          <Text style={styles.inlineErrorText}>Indtast din nuværende månedspris</Text>
                        </View>
                      )}
                    </>
                  )}

                  <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>Hastighedsbehov *</Text>
                  <View style={[styles.speedRow, fieldErrors.has('internet_speed') && { borderWidth: 1, borderColor: colors.error, borderRadius: borderRadius.md, padding: 4 }]}>
                    {INTERNET_SPEED_TIERS.map(tier => (
                      <Pressable
                        key={tier.value}
                        accessibilityRole="radio"
                        accessibilityState={{ selected: plan.needs === tier.value }}
                        accessibilityLabel={`${tier.label}: ${tier.desc}`}
                        style={[styles.speedCard, plan.needs === tier.value && styles.speedCardActive]}
                        onPress={() => { setInternetPlans(prev => prev.map((p, i) => i === idx ? { ...p, needs: tier.value } : p)); setFieldErrors(prev => { const n = new Set(prev); n.delete('internet_speed'); return n }) }}
                      >
                        <Text style={[styles.speedLabel, plan.needs === tier.value && { color: '#6b21a8' }]}>{tier.label}</Text>
                        <Text style={styles.speedDesc}>{tier.desc}</Text>
                      </Pressable>
                    ))}
                  </View>
                  {fieldErrors.has('internet_speed') && (
                    <View style={styles.inlineError}>
                      <AlertCircle size={12} color="#ef4444" />
                      <Text style={styles.inlineErrorText}>Vælg ønsket hastighed</Text>
                    </View>
                  )}

                  {/* Per-plan address */}
                  <View style={{ marginTop: spacing.sm }}>
                    <Pressable
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: plan.same_address }}
                      accessibilityLabel="Samme adresse"
                      style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}
                      onPress={() => setInternetPlans(prev => prev.map((p, i) => i === idx ? { ...p, same_address: !p.same_address } : p))}
                    >
                      <View style={[styles.checkbox, plan.same_address && styles.checkboxActive]}>
                        {plan.same_address && <Check size={10} color="#fff" />}
                      </View>
                      <Text style={styles.extraLabel}>Samme adresse</Text>
                    </Pressable>
                    {plan.same_address && sharedAddress ? (
                      <Text style={{ ...typography.small, color: colors.text.muted, marginTop: 4, marginLeft: 26 }}>{sharedAddress}</Text>
                    ) : !plan.same_address ? (
                      <View style={{ marginTop: spacing.xs, zIndex: 10 }}>
                        <AddressInput
                          value={plan.address}
                          onChange={val => setInternetPlans(prev => prev.map((p, i) => i === idx ? { ...p, address: val } : p))}
                          placeholder="Anden adresse for denne aftale..."
                        />
                      </View>
                    ) : null}
                  </View>

                  {/* "Har ikke endnu?" toggle */}
                  <Pressable
                    style={{ marginTop: spacing.sm, paddingVertical: 4 }}
                    onPress={() => setInternetPlans(prev => prev.map((p, i) => i === idx ? { ...p, hasExisting: !p.hasExisting, ...(p.hasExisting ? { provider: '', price: '' } : {}) } : p))}
                  >
                    <Text style={{ ...typography.small, color: colors.primary, textDecorationLine: 'underline' }}>
                      {plan.hasExisting !== false ? 'Har ikke internet endnu?' : 'Har allerede internet?'}
                    </Text>
                  </Pressable>
                </View>
              ))}

              <Pressable
                style={styles.addPlanButton}
                onPress={() => setInternetPlans(prev => {
                  const last = prev[prev.length - 1]
                  return [...prev, { provider: last?.provider || '', price: last?.price || '', same_address: false, address: '', needs: last?.needs || '300', hasExisting: true }]
                })}
              >
                <Plus size={16} color={colors.categories.internet} />
                <Text style={[styles.addPlanText, { color: colors.categories.internet }]}>Tilføj endnu en internet-aftale</Text>
              </Pressable>
            </Animated.View>
          )}

          {/* ===== STEP: Contact ===== */}
          {currentStepType === 'contact' && (
            <Animated.View key="contact" entering={FadeInRight.duration(300)} style={styles.card}>
              <Text style={styles.cardTitle}>Dine kontaktoplysninger</Text>
              <Text style={styles.cardSubtitle}>
                {userProfile?.full_name ? 'Vi har udfyldt fra dit MitID. Bekræft eller ret.' : 'Så vi kan oprette din personlige agent-email.'}
              </Text>

              <View style={styles.fieldGroup}>
                <Text style={styles.fieldLabel}>Fulde navn *</Text>
                <TextInput
                  style={[styles.textInput, fieldErrors.has('fullName') && { borderColor: colors.error }]}
                  placeholder="Dit fulde navn"
                  placeholderTextColor={colors.text.muted}
                  value={contactData.fullName}
                  onChangeText={val => { setContactData(prev => ({ ...prev, fullName: val })); setFieldErrors(prev => { const n = new Set(prev); n.delete('fullName'); return n }) }}
                  maxLength={200}
                />
                {fieldErrors.has('fullName') && (
                  <View style={styles.inlineError}>
                    <AlertCircle size={12} color="#ef4444" />
                    <Text style={styles.inlineErrorText}>Indtast dit fulde navn</Text>
                  </View>
                )}
              </View>

              <View style={styles.fieldGroup}>
                <Text style={styles.fieldLabel}>Email *</Text>
                <TextInput
                  style={[styles.textInput, fieldErrors.has('email') && { borderColor: colors.error }]}
                  placeholder="Email"
                  placeholderTextColor={colors.text.muted}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  value={contactData.email}
                  onChangeText={val => { setContactData(prev => ({ ...prev, email: val })); setFieldErrors(prev => { const n = new Set(prev); n.delete('email'); return n }) }}
                  maxLength={254}
                />
                {fieldErrors.has('email') && (
                  <View style={styles.inlineError}>
                    <AlertCircle size={12} color="#ef4444" />
                    <Text style={styles.inlineErrorText}>Indtast en gyldig email</Text>
                  </View>
                )}
              </View>

              <View style={styles.fieldGroup}>
                <Text style={styles.fieldLabel}>Telefon *</Text>
                <TextInput
                  style={[styles.textInput, fieldErrors.has('phone') && { borderColor: colors.error }]}
                  placeholder="12 34 56 78"
                  placeholderTextColor={colors.text.muted}
                  keyboardType="phone-pad"
                  value={contactData.phone}
                  onChangeText={val => {
                    const raw = val.replace(/[^0-9]/g, '')
                    const formatted = raw.replace(/(\d{2})(?=\d)/g, '$1 ').trim()
                    setContactData(prev => ({ ...prev, phone: formatted }))
                    setFieldErrors(prev => { const n = new Set(prev); n.delete('phone'); return n })
                  }}
                  maxLength={11}
                />
                {fieldErrors.has('phone') && (
                  <View style={styles.inlineError}>
                    <AlertCircle size={12} color="#ef4444" />
                    <Text style={styles.inlineErrorText}>Indtast dit 8-cifrede telefonnummer</Text>
                  </View>
                )}
              </View>

              {userProfile?.full_name && (
                <View style={styles.autoFillBanner}>
                  <ShieldCheck size={16} color={colors.warning} />
                  <Text style={styles.autoFillText}>Udfyldt automatisk fra dit MitID</Text>
                </View>
              )}

            </Animated.View>
          )}

          {/* ===== STEP: CPR ===== */}
          {currentStepType === 'cpr' && (
            <Animated.View key="cpr" entering={FadeInRight.duration(300)} style={styles.card}>
              <Text style={styles.cardTitle}>Dit CPR-nummer</Text>
              <Text style={[styles.cardSubtitle, { marginBottom: spacing.md }]}>
                Vi bruger dit CPR-nummer til at udfylde fuldmagten korrekt på dine vegne.
              </Text>

              {/* Warning box */}
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, backgroundColor: colors.warningMuted, borderRadius: borderRadius.sm, padding: spacing.sm, marginBottom: spacing.lg, borderWidth: 1, borderColor: '#fcd34d' }}>
                <ShieldCheck size={16} color="#92400e" style={{ marginTop: 1 }} />
                <Text style={{ ...typography.small, color: '#92400e', flex: 1 }}>
                  Dit CPR-nummer skal matche dit MitID. Det gemmes krypteret og bruges kun i forbindelse med skift — når du har accepteret et af vores forslag.
                </Text>
              </View>

              <Text style={styles.fieldLabel}>CPR-nummer *</Text>
              <TextInput
                style={[styles.textInput, { fontSize: 16 }, fieldErrors.has('cpr') || fieldErrors.has('cpr_invalid') ? { borderColor: colors.error } : {}]}
                value={cprInput}
                onChangeText={text => {
                  // Allow digits and dash only, max 11 chars (DDMMYY-XXXX)
                  const cleaned = text.replace(/[^0-9\-]/g, '').slice(0, 11)
                  setCprInput(cleaned)
                }}
                placeholder="DDMMÅÅ-XXXX"
                placeholderTextColor={colors.text.muted}
                keyboardType="numeric"
                maxLength={11}
                autoComplete="off"
                textContentType="none"
              />
              {(fieldErrors.has('cpr') || fieldErrors.has('cpr_invalid')) && (
                <View style={styles.inlineError}>
                  <AlertCircle size={13} color="#ef4444" />
                  <Text style={styles.inlineErrorText}>
                    {fieldErrors.has('cpr') ? 'Indtast dit 10-cifrede CPR-nummer' : 'CPR-nummeret ser ikke rigtigt ud — tjek det igen'}
                  </Text>
                </View>
              )}
            </Animated.View>
          )}

          {/* ===== STEP: Fuldmagt ===== */}
          {currentStepType === 'fuldmagt' && (
            <Animated.View key="fuldmagt" entering={FadeInRight.duration(300)} style={styles.card}>
              <View style={styles.fuldmagtHeader}>
                <LinearGradient
                  colors={['#3b82f6', '#a855f7']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{ width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' }}
                >
                  <ShieldCheck size={20} color="#fff" />
                </LinearGradient>
                <Text style={styles.cardTitle}>Godkend fuldmagt</Text>
              </View>
              <Text style={styles.cardSubtitle}>
                Med din fuldmagt kan vi kontakte selskaber, indhente tilbud og forhandle bedre priser.
              </Text>

              <View style={styles.fuldmagtItems}>
                <View style={styles.fuldmagtItem}>
                  <Mail size={16} color="#3b82f6" />
                  <Text style={styles.fuldmagtItemText}>Vi kontakter op til {selectedCategories.includes('energy') ? '49 strøm-, ' : ''}{selectedCategories.includes('mobile') ? '15 mobil- og ' : ''}{selectedCategories.includes('internet') ? '16 internetselskaber' : ''} for dig</Text>
                </View>
                <View style={styles.fuldmagtItem}>
                  <FileCheck size={16} color="#3b82f6" />
                  <Text style={styles.fuldmagtItemText}>Vi indhenter dine nuværende aftaledetaljer fra dit selskab</Text>
                </View>
                <View style={styles.fuldmagtItem}>
                  <ShieldCheck size={16} color={colors.warning} />
                  <Text style={styles.fuldmagtItemText}>Vi skifter aldrig uden din godkendelse</Text>
                </View>
                <View style={styles.fuldmagtItem}>
                  <X size={16} color={colors.text.muted} />
                  <Text style={styles.fuldmagtItemText}>Du kan trække fuldmagten tilbage når som helst</Text>
                </View>
              </View>

              {userProfile?.fuldmagt_accepted ? (
                <View style={{ padding: spacing.md, backgroundColor: colors.warningLight, borderRadius: borderRadius.md, borderWidth: 1, borderColor: '#fde68a', flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.sm }}>
                  <Check size={20} color={colors.warning} />
                  <View style={{ flex: 1 }}>
                    <Text style={{ ...typography.captionBold, color: colors.warningDark }}>Fuldmagt allerede i kraft</Text>
                    <Text style={{ ...typography.small, color: colors.warningDark, marginTop: 2 }}>Du har tidligere givet os fuldmagt — ingen grund til at gøre det igen.</Text>
                  </View>
                </View>
              ) : (
                <View style={{ marginBottom: spacing.xs }}>
                  <View style={styles.consentRow}>
                    <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: consentFuldmagt }} accessibilityLabel="Jeg giver fuldmagt" style={styles.consentCheckbox} onPress={() => {
                      const newVal = !consentFuldmagt
                      setConsentFuldmagt(newVal)
                      if (newVal) setConsentTerms(true)
                    }}>
                      <View style={[styles.checkbox, consentFuldmagt && styles.checkboxActive]}>
                        {consentFuldmagt && <Check size={12} color="#fff" />}
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{ ...typography.captionBold, color: colors.text.primary }}>Jeg giver fuldmagt</Text>
                        <Text style={{ ...typography.small, color: colors.text.muted, marginTop: 4 }}>
                          {`ForbrugerAgenten må kontakte selskaber og håndtere kommunikation på mine vegne for ${selectedCategories.map(c => c === 'energy' ? 'strøm' : c === 'mobile' ? 'mobil' : 'internet').join(', ')}.`}
                        </Text>
                      </View>
                    </Pressable>
                  </View>
                  <View style={styles.consentRow}>
                    <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: consentTerms }} accessibilityLabel="Jeg accepterer vilkårene" style={styles.consentCheckbox} onPress={() => setConsentTerms(!consentTerms)}>
                      <View style={[styles.checkbox, consentTerms && styles.checkboxActive]}>
                        {consentTerms && <Check size={12} color="#fff" />}
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{ ...typography.captionBold, color: colors.text.primary }}>Jeg accepterer vilkårene</Text>
                        <Text style={{ ...typography.small, color: colors.text.muted, marginTop: 4 }}>
                          <Text style={{ color: colors.primary, textDecorationLine: 'underline' }} onPress={() => Linking.openURL('https://forbrugeragenten.dk/betingelser')}>Vilkår</Text>
                          {' '}og{' '}
                          <Text style={{ color: colors.primary, textDecorationLine: 'underline' }} onPress={() => Linking.openURL('https://forbrugeragenten.dk/privatlivspolitik')}>privatlivspolitik</Text>
                        </Text>
                      </View>
                    </Pressable>
                  </View>
                </View>
              )}

              <View style={styles.consentRow}>
                <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: forwardToEmail }} accessibilityLabel="Send også kopi til min email" style={styles.consentCheckbox} onPress={() => setForwardToEmail(!forwardToEmail)}>
                  <View style={[styles.checkbox, forwardToEmail && styles.checkboxActive]}>
                    {forwardToEmail && <Check size={12} color="#fff" />}
                  </View>
                  <Text style={styles.consentText}>Send også kopi til {contactData.email || 'min email'}</Text>
                </Pressable>
              </View>

              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm, marginBottom: spacing.md }}>
                <Text style={{ ...typography.small, color: colors.text.muted }}>Jagt:</Text>
                {selectedCategories.map(cat => (
                  <View key={cat} style={{ paddingHorizontal: 10, paddingVertical: 3, backgroundColor: colors.categoriesBg[cat as keyof typeof colors.categoriesBg] || colors.primaryLight, borderRadius: borderRadius.full }}>
                    <Text style={{ ...typography.small, color: colors.categories[cat as keyof typeof colors.categories] || colors.primary, fontWeight: '600' }}>
                      {cat === 'energy' ? 'Strøm' : cat === 'mobile' ? 'Mobil' : 'Internet'}
                    </Text>
                  </View>
                ))}
              </View>
            </Animated.View>
          )}

          {/* ===== STEP: Forbind email ===== */}
          {currentStepType === 'email' && (
            <Animated.View key="email" entering={FadeInRight.duration(300)} style={styles.card}>
              <View style={styles.fuldmagtHeader}>
                <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: colors.primaryLight, borderWidth: 1, borderColor: '#bfdbfe', alignItems: 'center', justifyContent: 'center' }}>
                  <Mail size={20} color={colors.primary} />
                </View>
                <Text style={styles.cardTitle}>Forbind din email</Text>
              </View>
              <Text style={styles.cardSubtitle}>
                Forbind din email og få det fulde billede — vi overvåger automatisk og handler når noget sker. Vi læser kun emails fra dine selskaber — aldrig private beskeder.
              </Text>

              {!emailConnected && (
                <View style={{ gap: spacing.xs, marginBottom: spacing.md }}>
                  {[
                    { icon: <Mail size={15} color="#3b82f6" />, text: 'Vi fanger alle regninger og kvitteringer automatisk' },
                    { icon: <AlertCircle size={15} color="#f59e0b" />, text: 'Vi advarer dig øjeblikkeligt ved prisstigninger' },
                    { icon: <ShieldCheck size={15} color={colors.warning} />, text: 'Vi spotter uventede gebyrer og prisstigninger i dine regninger' },
                    { icon: <FileCheck size={15} color="#8b5cf6" />, text: 'Al kommunikation med selskaberne samlet ét sted' },
                  ].map((item, i) => (
                    <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 6, paddingHorizontal: spacing.sm, backgroundColor: colors.surface, borderRadius: borderRadius.sm }}>
                      {item.icon}
                      <Text style={{ ...typography.small, color: colors.text.secondary, flex: 1 }}>{item.text}</Text>
                    </View>
                  ))}
                </View>
              )}

              {emailConnected ? (
                <View style={{ gap: spacing.sm, marginBottom: spacing.sm }}>
                  <View style={{ padding: spacing.md, backgroundColor: colors.warningLight, borderRadius: borderRadius.md, borderWidth: 1, borderColor: '#fde68a', flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                    <CheckCircle size={20} color={colors.warning} />
                    <View style={{ flex: 1 }}>
                      <Text style={{ ...typography.captionBold, color: colors.warningDark }}>Email forbundet</Text>
                      <Text style={{ ...typography.small, color: colors.warningDark, marginTop: 2 }}>Vi samler al kommunikation fra selskaber i appen og rydder op i din indbakke.</Text>
                    </View>
                  </View>

                  <Text style={{ ...typography.small, color: colors.text.muted, marginTop: spacing.xs }}>
                    Vil du også modtage kopier på din email?
                  </Text>

                  {[
                    { key: 'forward_bills', label: 'Regninger' },
                    { key: 'forward_notices', label: 'Varsler' },
                    { key: 'forward_marketing', label: 'Reklamer' },
                  ].map(({ key, label }) => (
                    <Pressable
                      key={key}
                      style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.sm, backgroundColor: colors.background, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: colors.border }}
                      onPress={() => {
                        const current = (forwardPrefs as any)[key] ?? false
                        setForwardPrefs({ ...forwardPrefs, [key]: !current })
                      }}
                    >
                      <Text style={{ ...typography.caption, color: colors.text.primary }}>{label}</Text>
                      <View style={{ width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: (forwardPrefs as any)[key] ? colors.primary : colors.border, backgroundColor: (forwardPrefs as any)[key] ? colors.primary : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
                        {(forwardPrefs as any)[key] && <CheckCircle size={14} color="#fff" />}
                      </View>
                    </Pressable>
                  ))}

                  <Text style={{ ...typography.small, color: colors.text.muted, marginTop: 2 }}>
                    Du kan altid ændre dette i Indstillinger.
                  </Text>
                </View>
              ) : (
                <View style={{ gap: spacing.sm }}>
                  {/* Gmail — stor primær knap */}
                  <Pressable
                    style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md, padding: spacing.md, backgroundColor: colors.background, borderRadius: borderRadius.md, borderWidth: 2, borderColor: colors.primary, minHeight: 64 }}
                    onPress={() => handleConnectOAuthInline('google')}
                  >
                    <Image source={require('../../assets/provider-logos/gmail.png')} style={{ width: 36, height: 36, borderRadius: 8 }} />
                    <View style={{ flex: 1 }}>
                      <Text style={{ ...typography.captionBold, color: colors.text.primary, fontSize: 15 }}>Forbind Gmail</Text>
                      <Text style={{ ...typography.small, color: colors.text.muted }}>1-klik · Anbefalet</Text>
                    </View>
                    {connectingOAuth === 'google'
                      ? <ActivityIndicator size="small" color={colors.primary} />
                      : <ChevronRight size={18} color={colors.primary} />}
                  </Pressable>

                  {/* Outlook */}
                  <Pressable
                    style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md, padding: spacing.md, backgroundColor: colors.background, borderRadius: borderRadius.md, borderWidth: 2, borderColor: colors.border, minHeight: 64 }}
                    onPress={() => handleConnectOAuthInline('microsoft')}
                  >
                    <Image source={require('../../assets/provider-logos/outlook.png')} style={{ width: 36, height: 36, borderRadius: 8 }} />
                    <View style={{ flex: 1 }}>
                      <Text style={{ ...typography.captionBold, color: colors.text.primary, fontSize: 15 }}>Forbind Outlook</Text>
                      <Text style={{ ...typography.small, color: colors.text.muted }}>1-klik · Microsoft konto</Text>
                    </View>
                    {connectingOAuth === 'microsoft'
                      ? <ActivityIndicator size="small" color={colors.primary} />
                      : <ChevronRight size={18} color={colors.text.secondary} />}
                  </Pressable>

                  {/* Andre udbydere — inline logo tiles */}
                  <View style={{ borderRadius: borderRadius.md, borderWidth: 1, borderColor: colors.borderLight, backgroundColor: colors.surface, padding: spacing.sm }}>
                    <Text style={{ ...typography.small, color: colors.text.muted, marginBottom: spacing.sm, textAlign: 'center' }}>Andre udbydere</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, justifyContent: 'center' }}>
                      {[
                        { key: 'yousee', name: 'YouSee', logo: require('../../assets/provider-logos/yousee-dk.png') },
                        { key: 'telia', name: 'Telia', logo: require('../../assets/provider-logos/telia-dk.png') },
                        { key: 'telenor', name: 'Telenor', logo: require('../../assets/provider-logos/telenor-dk.png') },
                        { key: 'onecom', name: 'one.com', logo: require('../../assets/provider-logos/onecom.png') },
                        { key: 'stofa', name: 'Stofa', logo: require('../../assets/provider-logos/stofa-dk.png') },
                        { key: 'waoo', name: 'Waoo', logo: require('../../assets/provider-logos/waoo-dk.png') },
                        { key: 'jubii', name: 'Jubii', logo: require('../../assets/provider-logos/jubii.png') },
                        { key: 'fullrate', name: 'Fullrate', logo: require('../../assets/provider-logos/fullrate.png') },
                        { key: 'tre', name: '3', logo: require('../../assets/provider-logos/3-dk.png') },
                        { key: 'yahoo', name: 'Yahoo', logo: require('../../assets/provider-logos/yahoo.png') },
                        { key: 'icloud', name: 'iCloud', logo: require('../../assets/provider-logos/icloud.png') },
                      ].map(p => (
                        <Pressable key={p.key} onPress={() => { setInitialEmailProvider(p.key); setShowProviderModal(true) }}
                          style={({ pressed }) => ({ alignItems: 'center', gap: 6, opacity: pressed ? 0.7 : 1, width: 72, paddingVertical: spacing.xs })}>
                          <View style={{ width: 60, height: 60, borderRadius: 14, backgroundColor: colors.background, borderWidth: 1, borderColor: colors.borderLight, alignItems: 'center', justifyContent: 'center' }}>
                            <Image source={p.logo} style={{ width: 42, height: 42, borderRadius: 8 }} resizeMode="contain" />
                          </View>
                          <Text style={{ ...typography.small, color: colors.text.secondary, fontSize: 11, textAlign: 'center' }} numberOfLines={1}>{p.name}</Text>
                        </Pressable>
                      ))}
                      {/* Anden / ikke listet */}
                      <Pressable onPress={() => { setInitialEmailProvider(undefined); setShowProviderModal(true) }}
                        style={({ pressed }) => ({ alignItems: 'center', gap: 6, opacity: pressed ? 0.7 : 1, width: 72, paddingVertical: spacing.xs })}>
                        <View style={{ width: 60, height: 60, borderRadius: 14, backgroundColor: colors.background, borderWidth: 1, borderColor: colors.borderLight, alignItems: 'center', justifyContent: 'center' }}>
                          <Text style={{ fontSize: 24 }}>✉️</Text>
                        </View>
                        <Text style={{ ...typography.small, color: colors.text.secondary, fontSize: 11, textAlign: 'center' }} numberOfLines={2}>Anden udbyder</Text>
                      </Pressable>
                    </View>
                  </View>

                  {/* Skip — minimal */}
                  <Pressable
                    onPress={() => setShowSkipEmailModal(true)}
                    style={{ alignItems: 'center', paddingVertical: spacing.sm, marginTop: spacing.xs }}
                  >
                    <Text style={{ ...typography.small, color: colors.text.muted, textDecorationLine: 'underline' }}>
                      Fortsæt uden emailovervågning
                    </Text>
                  </Pressable>
                </View>
              )}
            </Animated.View>
          )}
        </ScrollView>

        {/* Navigation buttons */}
        <View style={[styles.navBar, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
          {step > 1 && (
            <PressableScale accessibilityRole="button" accessibilityLabel="Tilbage" style={styles.backButton} onPress={() => { haptics.light(); setStep(step - 1) }}>
              <ArrowLeft size={18} color={colors.text.secondary} />
              <Text style={styles.backText}>Tilbage</Text>
            </PressableScale>
          )}
          <View style={{ flex: 1 }} />

          {step < totalSteps ? (
            <PressableScale
              accessibilityRole="button"
              accessibilityLabel="Næste trin"
              style={[styles.nextButton,
                ((currentStepType === 'categories' && selectedCategories.length === 0) || loading) && { opacity: 0.5 }
              ]}
              onPress={handleNext}
              disabled={(currentStepType === 'categories' && selectedCategories.length === 0) || loading}
            >
              {loading && currentStepType === 'cpr' ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <Text style={styles.nextText}>Næste</Text>
                  <ArrowRight size={18} color="#fff" />
                </>
              )}
            </PressableScale>
          ) : (
            <PressableScale
              accessibilityRole="button"
              accessibilityLabel="Start jagten"
              style={[styles.nextButton, { backgroundColor: colors.ctaGreen }, (loading || (!isReturningUser && (!consentFuldmagt || !consentTerms))) && { opacity: 0.5 }]}
              onPress={handleSubmit}
              disabled={loading || (!isReturningUser && (!consentFuldmagt || !consentTerms))}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <Text style={styles.nextText}>Start Jagten!</Text>
                  <ArrowRight size={18} color="#fff" />
                </>
              )}
            </PressableScale>
          )}
        </View>
      </KeyboardAvoidingView>

      {/* Skip email confirmation modal */}
      <Modal visible={showSkipEmailModal} transparent animationType="fade" onRequestClose={() => setShowSkipEmailModal(false)}>
        <Pressable style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }} onPress={() => setShowSkipEmailModal(false)}>
          <Pressable onPress={e => e.stopPropagation()} style={{ backgroundColor: colors.background, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: spacing.xl, paddingBottom: spacing.xl + 16 }}>
            <View style={{ width: 40, height: 4, backgroundColor: colors.borderLight, borderRadius: 2, alignSelf: 'center', marginBottom: spacing.lg }} />
            <Text style={{ ...typography.h3, color: colors.text.primary, marginBottom: spacing.xs }}>Uden email mister du disse funktioner</Text>
            <Text style={{ ...typography.small, color: colors.text.muted, marginBottom: spacing.lg }}>Emailovervågning er det der giver agenten superkræfter. Uden den kan vi kun indhente tilbud — ikke overvåge.</Text>
            {[
              { text: 'Vi kan ikke opdage prisforhøjelser automatisk' },
              { text: 'Vi kan ikke fange ulovlige gebyrer i tide' },
              { text: 'Al kommunikation med selskaber forsvinder fra din indbakke' },
              { text: 'Du skal manuelt holde øje med regninger' },
            ].map((item, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.sm }}>
                <X size={18} color={colors.error} />
                <Text style={{ ...typography.small, color: colors.text.secondary, flex: 1 }}>{item.text}</Text>
              </View>
            ))}
            <Pressable onPress={() => setShowSkipEmailModal(false)} style={{ marginTop: spacing.lg, paddingVertical: 16, backgroundColor: colors.primary, borderRadius: borderRadius.md, alignItems: 'center' }}>
              <Text style={{ ...typography.captionBold, color: '#fff', fontSize: 16 }}>Forbind min email nu</Text>
            </Pressable>
            <Pressable onPress={() => { setConsentFuldmagt(true); setConsentTerms(true); setShowSkipEmailModal(false) }} style={{ marginTop: spacing.sm, paddingVertical: 12, alignItems: 'center' }}>
              <Text style={{ ...typography.small, color: colors.text.muted }}>Fortsæt alligevel med begrænset agent</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

      <EmailProviderModal
        visible={showProviderModal}
        onClose={() => { setShowProviderModal(false); setInitialEmailProvider(undefined) }}
        onConnected={handleEmailConnected}
        redirectTo="onboarding"
        consentGiven={true}
        initialProvider={initialEmailProvider}
      />
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.surface },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
  },
  headerTitle: { ...typography.h3, color: colors.text.primary },
  headerSubtitle: { ...typography.small, color: colors.text.muted },
  closeButton: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: colors.background, alignItems: 'center', justifyContent: 'center',
  },
  progressContainer: { paddingHorizontal: spacing.md, marginBottom: spacing.sm },
  progressLabels: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  progressStep: { ...typography.small, color: colors.primary, fontWeight: '600' },
  progressLabel: { ...typography.small, color: colors.text.muted },
  progressTrack: { height: 8, backgroundColor: colors.border, borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', backgroundColor: colors.primary, borderRadius: 4 },
  scrollView: { flex: 1 },
  scrollContent: { padding: spacing.md, paddingBottom: spacing.xl },

  card: {
    backgroundColor: colors.background, borderRadius: borderRadius.lg,
    padding: spacing.md, borderWidth: 1, borderColor: colors.border,
  },
  cardTitle: { ...typography.h3, color: colors.text.primary, marginBottom: spacing.xs },
  cardSubtitle: { ...typography.caption, color: colors.text.secondary, marginBottom: spacing.md, lineHeight: 20 },

  // Categories
  categoryCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    padding: spacing.md, borderRadius: borderRadius.md, borderWidth: 2,
    backgroundColor: colors.background,
  },
  categoryIconBox: {
    width: 48, height: 48, borderRadius: 12,
    alignItems: 'center', justifyContent: 'center',
  },
  categoryLabel: { ...typography.bodyBold, color: colors.text.primary, flex: 1 },
  disabledHint: { ...typography.small, color: colors.text.muted, marginTop: 2 },
  checkCircle: {
    width: 24, height: 24, borderRadius: 12,
    alignItems: 'center', justifyContent: 'center',
  },

  // How it works
  howItWorksItem: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    padding: spacing.md, backgroundColor: colors.surface, borderRadius: borderRadius.md, marginBottom: spacing.xs,
  },
  howItWorksNumber: {
    width: 36, height: 36, borderRadius: 18, backgroundColor: colors.primaryLight,
    alignItems: 'center', justifyContent: 'center',
  },
  howItWorksNumberText: { ...typography.captionBold, color: colors.primary },
  howItWorksTitle: { ...typography.bodyBold, color: colors.text.primary },
  howItWorksDesc: { ...typography.caption, color: colors.text.secondary },

  // Info banner
  infoBanner: {
    padding: spacing.md, backgroundColor: colors.primarySubtle, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#bfdbfe', marginBottom: spacing.md,
  },
  infoBannerText: { ...typography.caption, color: '#1e40af', lineHeight: 20 },

  // Fields
  fieldGroup: { marginBottom: spacing.md },
  fieldLabel: { ...typography.captionBold, color: colors.text.secondary, marginBottom: 4 },
  fieldHint: { ...typography.small, color: colors.text.muted, marginTop: 2 },
  inlineError: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 },
  inlineErrorText: { ...typography.small, color: colors.error },
  kwhWarning: { marginTop: 4, paddingHorizontal: 4 },
  kwhWarningText: { ...typography.small, color: colors.warning },
  textInput: {
    backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
    borderRadius: borderRadius.md, paddingHorizontal: spacing.sm, paddingVertical: 12,
    ...typography.caption, fontSize: 16, color: colors.text.primary, minHeight: 44,
  },

  // Plan cards
  planCard: {
    padding: spacing.md, borderRadius: borderRadius.md, borderWidth: 1, marginTop: spacing.sm,
  },
  planHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },

  // Category header
  categoryHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.sm },

  // Shared form styles (used by mobile plans, fuldmagt, etc.)
  extrasRow: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.sm },
  extraToggle: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  extraLabel: { ...typography.small, color: colors.text.secondary },
  checkbox: {
    width: 18, height: 18, borderRadius: 4, borderWidth: 1.5, borderColor: colors.border,
    alignItems: 'center', justifyContent: 'center',
  },
  checkboxActive: { backgroundColor: colors.primary, borderColor: colors.primary },

  // Pills
  pillRow: { flexDirection: 'row', gap: spacing.sm, marginTop: 4 },
  pill: {
    paddingHorizontal: spacing.sm, paddingVertical: 8, borderRadius: borderRadius.full,
    borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background,
  },
  pillActive: { backgroundColor: colors.warningMuted, borderColor: colors.warning },
  pillText: { ...typography.small, color: colors.text.muted },
  pillTextActive: { color: colors.warningDark, fontWeight: '600' },

  // GB grid
  gbGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: 4 },
  gbButton: {
    paddingHorizontal: spacing.sm, paddingVertical: 10, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background,
  },
  gbButtonActive: { backgroundColor: colors.primaryLight, borderColor: colors.primary },
  gbButtonText: { ...typography.small, color: colors.text.muted },
  gbButtonTextActive: { color: colors.primaryDark, fontWeight: '600' },

  // Speed
  speedRow: { flexDirection: 'row', gap: spacing.xs, marginTop: 4 },
  speedCard: {
    flex: 1, paddingVertical: spacing.sm, paddingHorizontal: spacing.xs, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background, alignItems: 'center',
  },
  speedCardActive: { backgroundColor: '#f3e8ff', borderColor: '#c084fc' },
  speedLabel: { ...typography.captionBold, color: colors.text.primary },
  speedDesc: { ...typography.small, color: colors.text.muted, textAlign: 'center', marginTop: 2 },

  // Add plan
  addPlanButton: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: spacing.sm, paddingVertical: spacing.xs },
  addPlanText: { ...typography.captionBold },

  // Agent email box
  agentEmailBox: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.md, backgroundColor: colors.primaryLight, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#bfdbfe', marginTop: spacing.md,
  },
  agentEmailLabel: { ...typography.caption, color: colors.primary },
  agentEmailValue: { ...typography.captionBold, color: colors.primaryDark },

  // MitID auto-fill banner
  autoFillBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingHorizontal: spacing.md, paddingVertical: 10,
    backgroundColor: colors.warningLight, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#fde68a', marginTop: spacing.md,
  },
  autoFillText: { ...typography.caption, color: colors.warningDark },

  // Fuldmagt
  fuldmagtHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  fuldmagtItems: { gap: spacing.xs, marginBottom: spacing.md },
  fuldmagtItem: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.md, backgroundColor: colors.surface, borderRadius: borderRadius.md,
  },
  fuldmagtItemText: { ...typography.caption, color: colors.text.secondary, flex: 1 },
  consentRow: { marginBottom: spacing.sm },
  consentCheckbox: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  consentText: { ...typography.caption, color: colors.text.primary, flex: 1, lineHeight: 20 },

  // Navigation bar
  navBar: {
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border,
    backgroundColor: colors.background,
  },
  backButton: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: spacing.sm },
  backText: { ...typography.caption, color: colors.text.secondary },
  nextButton: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: colors.primary, paddingHorizontal: spacing.lg, paddingVertical: 14,
    borderRadius: borderRadius.lg, minHeight: 48,
    ...shadows.colored(colors.primary),
  },
  nextText: { ...typography.bodyBold, color: '#fff' },
})
